import { cn } from '@dialogueconsulting/sked-ui/lib/utils'
import Bugsnag from '@bugsnag/js'
import isEmpty from 'lodash.isempty'
import uniqBy from 'lodash.uniqby'
import { useEffect, useState } from 'react'

import './InstagramGridPage.css'

import { fetchGridPreviewByAccount } from 'api'
import type { AccountSelectorProps } from 'Components'
import { PostPreviewGrid } from 'Components'
import Dropdown from 'Components/Common/Dropdown'
import PoweredBySked from 'Components/Common/PoweredBySked'
import { normalizeGridPreviewAllPosts } from 'normalizer'
import { ensureError } from 'utils/ensureError'
import getInstagramAccountOption from 'utils/getInstagramAccountOption'
import getReviewKeyFromURL from 'utils/getReviewKeyFromURL'

interface InstagramGridPageProps {
  accountSelectorProps: AccountSelectorProps
  isReadOnly: boolean
}

const InstagramGridPage = ({
  accountSelectorProps,
  isReadOnly,
}: InstagramGridPageProps) => {
  const reviewKey = getReviewKeyFromURL(window.location.href)

  const [gridData, setGridData] = useState<any>([])
  const [gridDataCached, setGridDataCached] = useState([])

  const [isGridPreviewLoading, setIsGridPreviewLoading] = useState(false)

  const [selectedAccountInfo, setSelectedAccountInfo] = useState({
    name: '',
    handler_name: '',
    avatar: '',
  })

  const accountDropdownOption = getInstagramAccountOption(
    accountSelectorProps.accounts
  )
  const accountIds = accountDropdownOption.map((acc) => acc.id)

  useEffect(() => {
    if (isEmpty(gridDataCached) && accountIds.length > 0) {
      const fetchData = async () => {
        setIsGridPreviewLoading(true)
        try {
          if (!accountIds || accountIds.length === 0) return

          const posts = await Promise.all(
            accountIds.map((id) => fetchGridPreviewByAccount(reviewKey, id))
          )

          const gridPosts = uniqBy(
            posts.filter((p) => Array.isArray(p)).flat(1),
            'id'
          )

          if (gridPosts.length > 0) {
            setGridData(
              normalizeGridPreviewAllPosts(gridPosts).filter((post: any) =>
                post.accounts.some((id: string) => {
                  return accountIds[0] === id
                })
              )
            )

            setGridDataCached(normalizeGridPreviewAllPosts(gridPosts))
          }
        } catch (error) {
          Bugsnag.notify(ensureError(error))
        } finally {
          setIsGridPreviewLoading(false)
        }
      }
      fetchData()
    }
  }, [gridDataCached, accountIds])

  useEffect(() => {
    if (gridData.length && gridData[0].accountInfo) {
      setSelectedAccountInfo(gridData[0].accountInfo)
    }
  }, [gridData])

  return (
    <div className="relative mx-auto my-6 w-[600px]">
      <h4 className="font-gotham text-xl font-extrabold text-neutral-800">
        Grid Preview
      </h4>
      <p
        className={cn(
          'text-sm font-medium text-neutral-600',
          accountIds.length <= 1 ? 'mb-0' : 'mb-6'
        )}
      >
        Upcoming posts for Instagram will appear in the grid preview.
      </p>
      {accountIds.length > 1 && (
        <div className="mx-auto w-[300px]">
          <Dropdown
            name="accounts"
            placeholder="Select from options"
            onChange={(selected) => {
              const selectedId = selected.value

              const nextGridData = gridDataCached.filter((post: any) =>
                post.accounts.some((id: string) => selectedId === id)
              )

              if (nextGridData.length > 0) {
                setGridData(nextGridData)
              } else {
                setGridData([])
              }
            }}
            options={accountDropdownOption}
          />
        </div>
      )}

      <div className="text-center text-base [&>p]:mt-6">
        {isGridPreviewLoading ? (
          <>
            <br />
            loading...
          </>
        ) : isEmpty(gridData) ? (
          <>
            <br />
            We couldn&apos;t find any upcoming posts
          </>
        ) : (
          <div className="ig-grid-preview">
            <PostPreviewGrid
              posts={gridData}
              account={selectedAccountInfo}
              isReadOnly={isReadOnly}
            />
          </div>
        )}
      </div>

      <PoweredBySked className="mt-6 flex justify-center" />
    </div>
  )
}

export default InstagramGridPage
